import {
  Favorite,
  LocationOn,
  Lock,
  Logout,
  Person,
  Share,
  ShoppingCart,
  Support,
} from "@mui/icons-material";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import LogoutConfirmDialog from "../common/LogoutConfirmDialog";
import { useLogoutConfirmation } from "../../../hooks/useLogoutConfirmation";
import { ProfileSection } from "../../../types/user";

interface ProfileSidebarProps {
  activeSection: ProfileSection;
  onSectionChange: (section: ProfileSection) => void;
}

const ProfileSidebar: React.FC<ProfileSidebarProps> = ({
  activeSection,
  onSectionChange,
}) => {
  const { t: myTranslate } = useTranslation();
  const {
    isLogoutDialogOpen,
    requestLogout,
    closeLogoutDialog,
    confirmLogout,
  } = useLogoutConfirmation();

  const menuItems = [
    {
      id: "personal" as ProfileSection,
      label: myTranslate("profile.personalInfo"),
      icon: <Person />,
    },
    {
      id: "orders" as ProfileSection,
      label: myTranslate("profile.orders"),
      icon: <ShoppingCart />,
    },
    {
      id: "wishlist" as ProfileSection,
      label: myTranslate("profile.wishlist"),
      icon: <Favorite />,
    },
    {
      id: "address" as ProfileSection,
      label: myTranslate("profile.address"),
      icon: <LocationOn />,
    },
    {
      id: "changePassword" as ProfileSection,
      label: myTranslate("profile.changePassword"),
      icon: <Lock />,
    },
    {
      id: "referral" as ProfileSection,
      label: myTranslate("profile.referral.title"),
      icon: <Share />,
    },
    {
      id: "support" as ProfileSection,
      label: myTranslate("profile.supportTicket"),
      icon: <Support />,
    },
  ];

  return (
    <>
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
          <Typography
            variant="h6"
            className="notranslate"
            translate="no"
            sx={{
              fontWeight: 600,
              color: "#374151",
              fontSize: "1.1rem",
            }}
          >
            {myTranslate("profile.yourDashboard")}
          </Typography>
        </Box>

        <List sx={{ p: 0 }}>
          {menuItems.map((item) => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                onClick={() => onSectionChange(item.id)}
                selected={activeSection === item.id}
                sx={{
                  py: 1.5,
                  px: 2,
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd",
                    borderRight: "3px solid #2563eb",
                    "& .MuiListItemIcon-root": {
                      color: "#2563eb",
                    },
                    "& .MuiListItemText-primary": {
                      color: "#2563eb",
                      fontWeight: 600,
                    },
                  },
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: activeSection === item.id ? "#2563eb" : "#6b7280",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <span className="notranslate" translate="no">
                      {item.label}
                    </span>
                  }
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.95rem",
                      color: activeSection === item.id ? "#2563eb" : "#374151",
                      fontWeight: activeSection === item.id ? 600 : 400,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}

          {/* Logout Button */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={requestLogout}
              sx={{
                py: 1.5,
                px: 2,
                "&:hover": {
                  backgroundColor: "#fef2f2",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: "#dc2626",
                }}
              >
                <Logout />
              </ListItemIcon>
              <ListItemText
                primary={
                  <span className="notranslate" translate="no">
                    {myTranslate("profile.logout")}
                  </span>
                }
                sx={{
                  "& .MuiListItemText-primary": {
                    fontSize: "0.95rem",
                    color: "#dc2626",
                    fontWeight: 400,
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Paper>

      <LogoutConfirmDialog
        open={isLogoutDialogOpen}
        onClose={closeLogoutDialog}
        onConfirm={confirmLogout}
      />
    </>
  );
};

export default ProfileSidebar;
